import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { useMaybePlayAudioOnceOnMount } from 'features/app-takeover/utils/play-audio-once';
import { ProductWizardStep as OneMenuProductWizardStep } from 'features/menu/components/product-wizard/types';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';

import { IItem } from '@rbi-ctg/menu';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { FlatCustomizationHeader } from 'components/product-customization/flat-customization-header';
import { useScrollToCustomization } from 'components/product-customization/static-layout';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductDetailHeader } from 'components/product-detail-v2/product-detail-flow/product-detail-header';
import {
  transformComboSlotTypeToEventAttr,
  transformProductWizardStepToEventAttr,
} from 'components/product-detail-v2/utils';
import { PageAnnouncer } from 'components/ui/page-announcer';
import { MenuObjectTypes } from 'enums/menu';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { isPicker } from 'utils/menu';

import { ProductCustomizationItemOptions } from '../product-customization-item-options';
import { IProductCustomizationItem } from '../product-customization-item/types';
import { ProductCustomizationModifiers } from '../product-customization-modifiers';
import { ProductSelectedItemCustomizationContainer } from '../styled';
import { SelectModifierFn } from '../types';
import { buildModifierGroups } from '../utils';

export const ProductCustomizationItem: React.FC<IProductCustomizationItem> = ({
  slotKeyToCustomize,
  comboSlot,
  comboSlotOptions,
  currentStep,
  selectedItem,
  handleComboSlotSelectionChange: onComboSlotSelectionChange,
  modifierSelections,
  defaultModifiersMap,
  handleChangeModifierSelections,
  handleResetModifierSelections,
  pickerAspectExclusions,
  onPressBackNavigation,
  backNavigationLabel = '',
}) => {
  const isMobile = useIsMobileBreakpoint();
  const scrollViewRef = useRef<ScrollView | null>(null);

  const { menuObject, updateProductWizardFlowSteps, comboSlotUpdate } = useProductWizardContext();
  const { isProductReviewFromCart } = useProductWizardFlow();
  const hasAvailableComboSlotOptions = comboSlotOptions.length > 1;

  const availableModifiers = useMemo(() => {
    if (!selectedItem?.options?.length) {
      return [];
    }

    return buildModifierGroups(selectedItem.options, modifierSelections, pickerAspectExclusions);
  }, [modifierSelections, pickerAspectExclusions, selectedItem?.options]);

  const hasModifiers = availableModifiers.length > 0;
  const hasInlineCustomization = hasAvailableComboSlotOptions && hasModifiers;

  const foundPickerFlowComboSlotUpdate = comboSlotUpdate.find(comboSlotUpdateVal => {
    return slotKeyToCustomize === comboSlotUpdateVal.id;
  });

  const { additionalAudioSteps } = useAppTakeoverPdp(menuObject?._id);
  useMaybePlayAudioOnceOnMount(
    additionalAudioSteps?.[OneMenuProductWizardStep.PickerCustomization]
  );

  const showBottomModifiers =
    (!!foundPickerFlowComboSlotUpdate?.isDirty ||
      comboSlotOptions.length <= 1 ||
      !isPicker(menuObject) ||
      isProductReviewFromCart) &&
    !hasInlineCustomization;

  const modifierCount = availableModifiers.length || 0;

  const {
    customizationRef,
    handleCustomizationLayout,
    canShowInlineCustomization,
    canMountCustomizationContent,
    onUserComboSlotSelection,
  } = useScrollToCustomization({
    scrollViewRef,
    scrollTrigger: selectedItem?._id,
    enabled: hasInlineCustomization,
    hasMultipleComboSlotOptions: comboSlotOptions.length > 1,
    modifierCount,
  });

  const handleComboSlotSelectionChange = useCallback(
    (comboSlotOption: Parameters<typeof onComboSlotSelectionChange>[0]) => {
      onComboSlotSelectionChange(comboSlotOption);
      onUserComboSlotSelection();
    },
    [onComboSlotSelectionChange, onUserComboSlotSelection]
  );

  const handleModifierSelectionsChange = useCallback<SelectModifierFn>(
    (selections, modifier) => {
      handleChangeModifierSelections(selections, modifier);
    },
    [handleChangeModifierSelections]
  );

  const modifierCustomizationProps = useMemo(
    () => ({
      modifierSelections,
      modifierDefaults: defaultModifiersMap,
      onModifierSelectionsChange: handleModifierSelectionsChange,
      onResetModifierSelections: handleResetModifierSelections,
      pickerAspectExclusions,
      selectedItem: selectedItem as IItem,
    }),
    [
      defaultModifiersMap,
      handleModifierSelectionsChange,
      handleResetModifierSelections,
      modifierSelections,
      pickerAspectExclusions,
      selectedItem,
    ]
  );

  const showInlineCustomization = canShowInlineCustomization && hasModifiers;

  const hasInlineCustomizationForGrid = useMemo(() => {
    if (!showInlineCustomization || !hasInlineCustomization || !selectedItem?._id) {
      return false;
    }

    return !!selectedItem?.options?.length;
  }, [
    hasInlineCustomization,
    selectedItem?._id,
    selectedItem?.options?.length,
    showInlineCustomization,
  ]);

  const inlineItemCustomization = useMemo(() => {
    if (!hasInlineCustomizationForGrid) {
      return null;
    }

    return (
      <View
        key={`inline-customization-${selectedItem?._id}`}
        ref={customizationRef}
        collapsable={false}
        onLayout={handleCustomizationLayout}
        style={styles.inlineCustomization}
      >
        <ProductSelectedItemCustomizationContainer>
          <FlatCustomizationHeader />
          {canMountCustomizationContent ? (
            <ProductCustomizationModifiers
              {...modifierCustomizationProps}
              hasMultipleComboSlotOptions
            />
          ) : null}
        </ProductSelectedItemCustomizationContainer>
      </View>
    );
  }, [
    canMountCustomizationContent,
    customizationRef,
    handleCustomizationLayout,
    hasInlineCustomizationForGrid,
    modifierCustomizationProps,
    selectedItem?._id,
  ]);

  useEffect(() => {
    updateProductWizardFlowSteps({
      id: slotKeyToCustomize,
      type: MenuObjectTypes.ITEM,
      currentStep: true,
    });
  }, [slotKeyToCustomize, updateProductWizardFlowSteps]);

  const productDetailHeaderTitle = useMemo(() => {
    const shouldDisplaySelectedItemNameTitle =
      !isPicker(menuObject) || currentStep === ProductWizardStep.ProductEditOrder;

    if (shouldDisplaySelectedItemNameTitle && selectedItem?.name.locale) {
      return selectedItem.name.locale;
    }

    return t('haveItYourWay');
  }, [currentStep, menuObject, selectedItem?.name.locale]);

  if (!selectedItem) {
    return null;
  }

  return (
    <>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={ProductWizardStep.ProductCustomization}
        onPress={onPressBackNavigation}
        backNavigationLabel={backNavigationLabel}
        eventAttributes={{
          name: transformProductWizardStepToEventAttr(currentStep),
          comboSlotType: transformComboSlotTypeToEventAttr('item'),
        }}
      />

      <View style={styles.container}>
        <ScrollView
          ref={scrollViewRef}
          showsVerticalScrollIndicator
          removeClippedSubviews={Platform.OS === 'android'}
        >
          <LayoutContainer isFullContainer={isMobile}>
            <InnerLayout>
              {hasAvailableComboSlotOptions ? (
                <ProductCustomizationItemOptions
                  comboSlot={comboSlot}
                  comboSlotOptions={comboSlotOptions}
                  selectedItem={selectedItem as IItem}
                  onSelectComboSlot={handleComboSlotSelectionChange}
                  inlineItemCustomization={inlineItemCustomization}
                  showInlineCustomization={canShowInlineCustomization}
                />
              ) : null}

              {showBottomModifiers ? (
                <ProductCustomizationModifiers {...modifierCustomizationProps} />
              ) : null}
            </InnerLayout>
          </LayoutContainer>
        </ScrollView>
      </View>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
        selectedItem={selectedItem}
      />

      <PageAnnouncer pageTitle={productDetailHeaderTitle} />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  inlineCustomization: {
    width: '100%',
  },
});
